import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { Mail, Save, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "./useApi";

export function EmailSettingsCard({ token, t }) {
  var [emailSettings, setEmailSettings] = useState({ sendgrid_api_key: "", sender_email: "", sender_name: "Property Central", app_url: "", notifications_enabled: false, has_api_key: false, notify_trial_ending: true, notify_payment_due: true, notify_payment_overdue: true, notify_account_deactivated: true, notify_account_reactivated: true });
  var [savingEmail, setSavingEmail] = useState(false);
  var [testingEmail, setTestingEmail] = useState(false);
  var { fetchJson, putJson, fetchApi } = useApi(token);

  useEffect(function() { fetchEmailSettings(); }, []);

  async function fetchEmailSettings() {
    try { var data = await fetchJson("/api/admin/settings/email"); setEmailSettings(data); } catch (err) {}
  }

  async function handleSaveEmailSettings() {
    setSavingEmail(true);
    try {
      var response = await putJson("/api/admin/settings/email", emailSettings);
      if (response.ok) { toast.success(t("settings.emailUpdated")); fetchEmailSettings(); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingEmail(false); }
  }

  async function handleTestEmail() {
    setTestingEmail(true);
    try {
      var response = await fetchApi("/api/admin/settings/email/test", { method: "POST" });
      if (response.ok) { var data = await response.json(); toast.success(t("settings.testEmailSent", { email: data.sent_to })); }
      else { var error = await response.json(); toast.error(error.detail || t("settings.testEmailFailed")); }
    } catch (err) { toast.error(t("settings.testEmailFailed")); }
    finally { setTestingEmail(false); }
  }

  return (
    <Card className="bento-card border-blue-500/50" data-testid="email-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-500" />{t("settings.emailSettings")}</CardTitle>
        <CardDescription>{t("settings.emailSettingsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <div><p className="font-medium">{t("settings.enableNotifications")}</p><p className="text-sm text-muted-foreground">{t("settings.enableNotificationsDesc")}</p></div>
          <Switch checked={emailSettings.notifications_enabled} onCheckedChange={function(v) { setEmailSettings({ ...emailSettings, notifications_enabled: v }); }} data-testid="email-notifications-switch" />
        </div>
        <Separator />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="form-label">{t("settings.sendgridApiKey")}</Label>
            <Input type="password" value={emailSettings.sendgrid_api_key} onChange={function(e) { setEmailSettings({ ...emailSettings, sendgrid_api_key: e.target.value }); }} placeholder={emailSettings.has_api_key ? t("settings.apiKeySet") : t("settings.enterApiKey")} data-testid="sendgrid-api-key-input" />
          </div>
          <div className="space-y-2">
            <Label className="form-label">{t("settings.senderEmail")}</Label>
            <Input type="email" value={emailSettings.sender_email} onChange={function(e) { setEmailSettings({ ...emailSettings, sender_email: e.target.value }); }} placeholder="noreply@yourdomain.com" data-testid="sender-email-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="form-label">{t("settings.senderName")}</Label>
            <Input value={emailSettings.sender_name} onChange={function(e) { setEmailSettings({ ...emailSettings, sender_name: e.target.value }); }} placeholder="Property Central" data-testid="sender-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="form-label">{t("settings.appUrl")}</Label>
            <Input type="url" value={emailSettings.app_url} onChange={function(e) { setEmailSettings({ ...emailSettings, app_url: e.target.value }); }} placeholder="https://yourdomain.com" data-testid="app-url-input" />
          </div>
        </div>
        <Separator />
        <div className="space-y-3">
          <Label className="form-label">{t("settings.notificationTypes")}</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: "notify_trial_ending", label: "trialEnding" },
              { key: "notify_payment_due", label: "paymentDue" },
              { key: "notify_payment_overdue", label: "paymentOverdue" },
              { key: "notify_account_deactivated", label: "accountDeactivated" },
              { key: "notify_account_reactivated", label: "accountReactivated" },
            ].map(function(item) {
              return (
                <div key={item.key} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                  <span className="text-sm">{t("settings." + item.label)}</span>
                  <Switch checked={emailSettings[item.key]} onCheckedChange={function(v) { var update = {}; update[item.key] = v; setEmailSettings(function(prev) { return { ...prev, ...update }; }); }} />
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleSaveEmailSettings} disabled={savingEmail} data-testid="save-email-btn">
            {savingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {savingEmail ? t("settings.saving") : t("settings.saveEmailSettings")}
          </Button>
          <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail || !emailSettings.has_api_key} data-testid="test-email-btn">
            {testingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {testingEmail ? t("settings.sending") : t("settings.sendTestEmail")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
